import React from "react";
import { Authentication } from "@hex-insights/app-modules";
import { Column, Conditional, Else, Heading, If, Image, Paragraph, Row } from "@hex-insights/core";
import { Form, FormState, SubmissionStatus, SubmitButton, TextField } from "@hex-insights/forms";
import { InternalLink, useActivePageRegistration } from "@hex-insights/router";
import { serverRequest, StaticURLs } from "@hex-insights/verita-romania.shared";
import { Main } from "../../../Components";
import { loginPageInfo } from "../Login/pageinfo";
import { recoverPageInfo } from "./pageinfo";
import styles from "../authentication.module.css";

export function RecoverPage() {
	useActivePageRegistration(recoverPageInfo);

	const [isDone, setIsDone] = React.useState(false);
	const [submittedEmail, setSubmittedEmail] = React.useState("");

	const onSuccess = React.useCallback(({ formValues: { email } }: FormState<Authentication.RecoverFormValues>) => {
		setSubmittedEmail(email);
		setIsDone(true);
	}, []);

	return (
		<Main className={styles["auth-page__main"]}>
			<Column
				justify="spaced-center"
				align="center"
				verticalSpacing="1.5rem"
				className={styles["auth-page__content-container"]}
			>
				<Image src={StaticURLs.Images.veritaWingsSymmetricSmall} />

				<Heading.H1 noMargin>Recover Account</Heading.H1>

				<div style={{ maxWidth: "100%" }}>
					<Conditional>
						<If condition={isDone}>
							<Paragraph>Recovery email sent to {submittedEmail}. Please allow a few minutes for arrival.</Paragraph>

							<Paragraph>
								<InternalLink
									to={{
										pathname: loginPageInfo.to,
										state: { formValues: { email: submittedEmail } },
									}}
									className="link--primary link--no-text-decoration"
									style={{ fontWeight: "bold" }}
								>
									Return to login page
								</InternalLink>
							</Paragraph>
						</If>

						<Else>
							<RecoverForm onSuccess={onSuccess} />
						</Else>
					</Conditional>
				</div>
			</Column>
		</Main>
	);
}

type RecoverFormProps = {
	onSuccess: (formState: FormState<Authentication.RecoverFormValues>) => void;
};

function RecoverForm({ onSuccess }: RecoverFormProps) {
	const { formState, onSubmit, linkState } = Authentication.useRecover({ requestRecover, onSuccess });

	return (
		<Form formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} noNotifications noNavigationPrompt>
			<Column justify="spaced-start" verticalSpacing="1.5rem">
				<If condition={SubmissionStatus.isFailure(formState.submissionStatus)}>
					<Paragraph>{formState.formSubmissionErrors._.join(" ")}</Paragraph>
				</If>

				<TextField formState={formState} name="email" autoFocus inputMode="email" style={{ maxWidth: "100%" }} />

				<Row justify="space-between" align="flex-end">
					<InternalLink
						to={{ pathname: loginPageInfo.to, state: linkState }}
						className="link--primary link--no-text-decoration"
						style={{ fontWeight: "bold" }}
					>
						Cancel
					</InternalLink>

					<SubmitButton
						submissionStatus={formState.submissionStatus}
						onClick={formState.onSubmitWrapper(onSubmit)}
						style={{ fontSize: "1rem", fontWeight: "bold" }}
					>
						Recover
					</SubmitButton>
				</Row>
			</Column>
		</Form>
	);
}

function requestRecover(formValues: Authentication.RecoverFormValues) {
	return serverRequest("/auth/recover", {
		method: "POST",
		body: JSON.stringify(formValues),
	});
}
